import {
  BackButtonCWUI,
  ButtonUI,
  CustomerCategoryTitleUI,
} from '@next-order/next-order-components';
import AnimationWrapper from 'components/AnimationWrapper';
import { useRef, useReducer, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectStoreConfig } from 'containers/Layout/selectors';
import exclamationMark from '../../assets/images/exclamationmark.svg';
import { firebaseAnalyticsNO } from '../../firebase';
import { eventNames } from 'configuration/eventNames';
import AdyenSaveCardDropin from 'components/OrderPayment/AdyenSaveCardDropin';
import { CheckCircleIcon } from '@heroicons/react/solid';

const AUTO_HIDE_DURATION = 4000;

const PaymentMethods = ({ userDetails, onBack, fetchCreditCardsAdyen }) => {
  const dropinRef = useRef(null);
  const storeConfig = useSelector(selectStoreConfig);
  const [showScrollShadow, setShowScrollShadow] = useState(false);
  const [localState, setLocalState] = useReducer((s, a) => ({ ...s, ...a }), {
    moveNextDetail: 0,
    paymentInProcess: false,
    keyBoard: false,
    placeholderValue: 'MM/YY',
    isCardError: false,
    isError: false,
    errorMessage: '',
    clientSecret: '',
    isAdditionalDetailsInProcess: false,
    cardPaymentInProcess: false,
    failedOperation: '',
    successMessage: '',
    isSuccess: false,
  });

  const handleResetAdyen = () => {
    setLocalState({
      isAdditionalDetailsInProcess: false,
      cardPaymentInProcess: false,
      isCardError: false,
      isError: false,
      errorMessage: '',
      failedOperation: '',
      successMessage: '',
      isSuccess: false,
    });
    if (dropinRef.current) {
      dropinRef.current?.resetDropin();
    }
  };

  const onSubmit = async (state, component, actions) => {
    setLocalState({
      isAdditionalDetailsInProcess: false,
      isCardError: false,
      isError: false,
      errorMessage: '',
      paymentInProcess: true,
      failedOperation: 'save',
      successMessage: '',
      isSuccess: false,
    });
    component.setStatus('loading');
    const { data, isValid } = state || {};
    const { paymentMethod } = data || {};

    if (!isValid || !paymentMethod) {
      console.warn('Adyen form is invalid');
      actions.reject();
      component.setStatus('ready');
      return;
    }

    try {
      const response = await fetch(
        `${storeConfig.cloudFunctionsUrl}/adyenCards`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentMethod,
            storeId: storeConfig.storeId,
            userId: userDetails?._id,
          }),
        }
      );

      const data = await response.json();

      if (data?.payload?.action) {
        component.handleAction(data.payload.action);
        setTimeout(() => {
          setLocalState({ cardPaymentInProcess: true });
        }, 0);
        return;
      }

      if (data?.success === true) {
        actions.resolve();
        component.setStatus('success');
        setLocalState({
          isSuccess: true,
          successMessage: 'Card saved successfully',
          isError: false,
          errorMessage: '',
        });
        if (typeof fetchCreditCardsAdyen === 'function') {
          fetchCreditCardsAdyen(
            userDetails?._id,
            storeConfig?.storeId,
            true,
            true
          );
        }
        firebaseAnalyticsNO.logEvent(eventNames.SAVED_CARD);
        setTimeout(() => {
          handleResetAdyen();
        }, AUTO_HIDE_DURATION);
        return;
      }

      actions.reject();
      setLocalState({
        isCardError: true,
        isError: true,
        errorMessage: data?.message || 'An error occurred while storing card',
        paymentInProcess: false,
      });
      component.setStatus('ready');
    } catch (err) {
      console.error('Card store error:', err);
      actions.reject();
      setLocalState({
        isCardError: true,
        isError: true,
        errorMessage: 'An error occurred while storing card',
        paymentInProcess: false,
      });
      component.setStatus('ready');
    }
  };

  const onAdditionalDetails = async (state, component, actions) => {
    component.setStatus('loading');
    setLocalState({
      isAdditionalDetailsInProcess: true,
    });
    try {
      if (state.data) {
        const response = await fetch(
          `${storeConfig.cloudFunctionsUrl}/adyenPaymentDetails`,
          {
            method: 'post',
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ...state.data,
              storeId: storeConfig.storeId,
              userId: userDetails?._id,
            }),
          }
        );
        const data = await response.json();

        actions.resolve();

        if (data?.success) {
          actions.resolve();
          component.setStatus('success');
          setLocalState({
            isSuccess: true,
            successMessage: 'Card saved successfully',
            isError: false,
            errorMessage: '',
          });
          if (typeof fetchCreditCardsAdyen === 'function') {
            fetchCreditCardsAdyen(
              userDetails?._id,
              storeConfig?.storeId,
              true,
              true
            );
          }
          firebaseAnalyticsNO.logEvent(eventNames.SAVED_CARD);
          setTimeout(() => {
            handleResetAdyen();
          }, AUTO_HIDE_DURATION);
          return;
        }
        actions.reject();
        setLocalState({
          isCardError: true,
          isError: true,
          errorMessage: data?.message || 'An error occurred while storing card',
          paymentInProcess: false,
        });
        component.setStatus('ready');
      }
    } catch (error) {
      actions.reject();
      setLocalState({
        isCardError: true,
        isError: true,
        errorMessage: 'An error occurred during payment',
        paymentInProcess: false,
      });
      component.setStatus('ready');
    } finally {
      setLocalState({
        isAdditionalDetailsInProcess: false,
      });
    }
  };

  const onPaymentCompleted = (result) => {
    console.log('onPaymentCompleted: ', result);
  };

  const onFailedorError = (from, data) => {
    console.error(from, data);
    setLocalState({
      isCardError: true,
      isError: true,
      errorMessage: 'An error occurred during payment',
      paymentInProcess: false,
      cardPaymentInProcess: false,
    });
  };

  const onPaymentFailed = (result) => {
    onFailedorError('onPaymentFailed: ', result);
  };

  const onError = (error) => {
    onFailedorError('onError: ', error);
  };

  const onDisableStoredPaymentMethod = async (
    storedPaymentMethodId,
    resolve,
    reject
  ) => {
    try {
      setLocalState({
        isError: false,
        errorMessage: '',
        isCardError: false,
        failedOperation: 'delete',
        successMessage: '',
        isSuccess: false,
      });

      const response = await fetch(
        `${storeConfig.cloudFunctionsUrl}/adyenCards`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storedPaymentMethodId,
            storeId: storeConfig.storeId,
            userId: userDetails?._id,
          }),
        }
      );

      const data = await response.json();

      if (data?.success === true) {
        resolve();
        setLocalState({
          isSuccess: true,
          successMessage: 'Card deleted successfully',
          isError: false,
          errorMessage: '',
        });

        if (typeof fetchCreditCardsAdyen === 'function') {
          fetchCreditCardsAdyen(
            userDetails?._id,
            storeConfig?.storeId,
            true,
            true
          );
        }
        firebaseAnalyticsNO.logEvent(eventNames.DELETED_CARD);
        setTimeout(() => {
          handleResetAdyen();
        }, AUTO_HIDE_DURATION);
        return;
      }

      reject();
      setLocalState({
        isError: true,
        errorMessage: data?.message || 'An error occurred while deleting card',
        isCardError: true,
      });
    } catch (err) {
      console.error('Card delete error:', err);
      reject();
      setLocalState({
        isError: true,
        errorMessage: 'An error occurred while deleting card',
        isCardError: true,
      });
    }
  };

  const handleScroll = (e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Show shadow when there's scrollable content and user has scrolled
    const isScrollable = scrollHeight > clientHeight;
    const hasScrolled = scrollTop > 5;

    setShowScrollShadow(isScrollable && hasScrolled);
  };

  useEffect(() => {
    const setRealViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setRealViewportHeight();
    window.addEventListener('resize', setRealViewportHeight);

    return () => {
      window.removeEventListener('resize', setRealViewportHeight);
    };
  }, []);

  return (
    <AnimationWrapper
      animation='fade-in'
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <aside className='sidebar'>
        <div className='sidebar__wrapper'>
          <div className='card__about'>
            <div className='relative h-screen pt-4'>
              <BackButtonCWUI onClick={onBack} extendedClassName='mx-5' />
              <div className='relative pb-4'>
                <CustomerCategoryTitleUI extendedClassName='mt-3.25 !mb-0 ml-[-1px] px-5'>
                  Payment Methods
                </CustomerCategoryTitleUI>
                {/* Shadow below title when scrolled */}
                <div
                  className={`absolute left-0 right-0 top-full h-3 pointer-events-none transition-opacity duration-200 z-10 ${
                    showScrollShadow ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)',
                  }}
                />
              </div>

              <div
                className='saved-card overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+60px)] px-5 h-[calc(var(--vh)_*_100_-_180px)] lg:h-[calc(100vh-120px)]'
                onScroll={handleScroll}
              >
                {localState.isSuccess && (
                  <div className='flex items-center justify-start gap-y-2 px-4 py-3 mb-5 rounded-lg bg-green-50 border border-green-200'>
                    <CheckCircleIcon
                      className='h-8 w-8 text-green-400 mr-2'
                      aria-hidden='true'
                    />
                    <span className='text-[14px] font-medium text-green-600'>
                      {localState.successMessage}
                    </span>
                  </div>
                )}
                {localState.isError && (
                  <div className='cardCommon flex flex-col items-start gap-y-2 p-4 mb-5 rounded-lg'>
                    <div>
                      <img
                        src={exclamationMark}
                        alt='error'
                        className='h-[20px]'
                      />
                    </div>
                    <span className='redSpan text-[14px] font-medium'>
                      {localState.errorMessage
                        ? `${localState.errorMessage}`
                        : ''}
                    </span>
                    {localState.isCardError && (
                      <>
                        <span className='text-[14px]'>
                          Please double check your card details or try a
                          different payment method.
                        </span>
                        <span className='text-[14px]'>
                          If the issue persists, contact your card issuer and
                          try again later.
                        </span>
                        <ButtonUI
                          onClick={handleResetAdyen}
                          btnType='reset'
                          size='lg'
                          extendedClassName='w-full !bg-black !border-black hover:text-white hover:opacity-90 mt-2 h-[42px] rounded-xl'
                        >
                          {localState.failedOperation === 'save'
                            ? 'Retry Saving Card'
                            : 'Retry Deleting Card'}
                        </ButtonUI>
                      </>
                    )}
                  </div>
                )}

                <AdyenSaveCardDropin
                  ref={dropinRef}
                  isCardPaymentAvailable={true}
                  onSubmit={onSubmit}
                  onAdditionalDetails={onAdditionalDetails}
                  onPaymentCompleted={onPaymentCompleted}
                  onPaymentFailed={onPaymentFailed}
                  onError={onError}
                  onDisableStoredPaymentMethod={onDisableStoredPaymentMethod}
                  shouldHide={localState.isError}
                  isSuccess={localState.isSuccess}
                />
              </div>
            </div>
          </div>
        </div>
      </aside>
    </AnimationWrapper>
  );
};

export default PaymentMethods;
